import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from './../../AuthContext/AuthContext';
import { BASE_URL } from './../../Api/ApiUrls';

const usePropertyActions = () => {
  const { userId } = useContext(AuthContext);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [savedProperties, setSavedProperties] = useState([]);
  const [saving, setSaving] = useState({});

  // Check subscription status
  const checkSubscription = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/Get-user-plan/${userId}/`);
      const userSubscriptions = response.data;
      const hasBuyerSubscription = Array.isArray(userSubscriptions) &&
        userSubscriptions.some(sub => sub.user_type === "Buyer");
      setIsSubscribed(hasBuyerSubscription);
    } catch (error) {
      console.error('Error checking subscription:', error);
      setIsSubscribed(false);
    }
  };

  // Fetch saved properties
  const fetchSavedProperties = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/get-user-cart/${userId}/`);
      const savedItems = Array.isArray(response.data) ? response.data : [];
      setSavedProperties(savedItems.map(item => String(item.property_id)));
    } catch (error) {
      console.error('Error fetching saved properties:', error);
      setSavedProperties([]);
    }
  };

  // Add to cart
  const addToCart = async (propertyId) => {
    try {
      const response = await axios.post(`${BASE_URL}/user-cart/`, {
        user_id: userId,
        property_id: propertyId
      });
      return response.data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  };

  // Remove from cart
const removeFromCart = async (propertyId) => {
  try {
    
    // Get all cart items for user
    const cartResponse = await axios.get(`${BASE_URL}/get-user-cart/${userId}/`);
    
    const cartItems = Array.isArray(cartResponse.data) ? cartResponse.data : [];
    
    // Find matching cart item
    const cartItem = cartItems.find(
      item => String(item.property_id) === String(propertyId)
    );

    if (cartItem) {
      await axios.delete(`${BASE_URL}/user-cart/${cartItem.cart_id}/`);
      console.log('✅ Deleted cart item:', cartItem.cart_id);
    } else {
      console.log('❌ No cart item found for propertyId:', propertyId);
    }
  } catch (error) {
    console.error('Error removing from cart:', error);
    throw error;
  }
};

  // Toggle save (uses addToCart and removeFromCart)
  const toggleSave = async (property) => {
    const propertyId = property.id;
    setSaving(prev => ({ ...prev, [propertyId]: true }));
    try {
      const isSaved = savedProperties.includes(propertyId);
      if (isSaved) {
        await removeFromCart(propertyId);
        setSavedProperties(prev => prev.filter(id => id !== propertyId));
      } else {
        await addToCart(propertyId);
        setSavedProperties(prev => [...prev, propertyId]);
      }
    } catch (error) {
      console.error('Error updating saved properties:', error);
    } finally {
      setSaving(prev => ({ ...prev, [propertyId]: false }));
    }
  };

  // Check if a property is saved
  const isSaved = (property) => savedProperties.includes(property.id);

  // Effect to check subscription and fetch saved properties on mount or userId change
  useEffect(() => {
    if (userId) {
      checkSubscription();
      fetchSavedProperties();
    } else {
      setIsSubscribed(false);
      setSavedProperties([]);
    }
  }, [userId]);

  return {
    isSubscribed,
    savedProperties,
    saving,
    checkSubscription,
    fetchSavedProperties, 
    addToCart,
    removeFromCart,
    toggleSave,
    isSaved
  };
};

export default usePropertyActions;